import { MenuItem, TableToolbar, TableToolbarContent } from "@carbon/react";
import { NullableTableColumns, useModal } from "@flaminem/ui-components";
import React from "react";
import { useParams } from "react-router";

import { AuthorizedItem, AuthorizedMenu } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import { DimensionedLayout } from "src/components/DimensionedLayout/DimensionedLayout.tsx";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions.ts";
import { Highlight } from "src/components/Highlight/Highlight.tsx";
import MenuButton from "src/components/MenuButton/MenuButton.tsx";
import { ModalTransition } from "src/components/Modal";
import { FixedTable } from "src/components/Tables/FixedTable";
import { TableToolbarSearch } from "src/components/Tables/TableToolbarSearch";
import { useGetRulesQuery } from "src/graphql/anti-fraud/queries/rules/GetRulesQuery.ts";
import { useGetRuleImplementationTypeLabel } from "src/hooks/rules/useGetRuleImplementationTypeLabel";
import { useLocalSearchByFields } from "src/hooks/useLocalSearch.ts";
import { useT } from "src/internationalization";
import { Rule } from "src/types/rule";

import { CreateRuleAdvancedModal } from "./create/CreateRuleAdvancedModal";
import { CreateRuleModal } from "./create/CreateRuleModal.tsx";
import { CreateLinkedCaseRuleModal } from "./linkedCase/CreateLinkedCaseRuleModal.tsx";
import { RuleActionsMenu } from "./RulesActionsMenu/RuleActionsMenu.tsx";

const RulesTab: React.FC = () => {
  const t = useT();
  const { pageSize, dimensions } = useDimensions();

  const params = useParams<{ code: string }>();
  const caseTypeCode = params.code ? params.code : "";
  const getRuleImplementationTypeLabel = useGetRuleImplementationTypeLabel();
  const {
    data: rules,
    loading,
    error,
  } = useGetRulesQuery({
    variables: params.code
      ? {
          caseTypeCode: params.code,
        }
      : undefined,
  });

  const { filter, handleSearch, searchTerm } = useLocalSearchByFields([
    "name",
    "code",
    "description",
    "implementationType",
  ]);

  // create modal
  const {
    isModalOpen: isCreateModalOpen,
    openModal: openCreateModal,
    closeModal: closeCreateModal,
  } = useModal();

  const {
    isModalOpen: isCreateAdvancedModalOpen,
    openModal: openCreateAdvancedModal,
    closeModal: closeCreateAdvancedModal,
  } = useModal();

  const {
    isModalOpen: isCreateDifferentModalOpen,
    openModal: openCreateDifferentModal,
    closeModal: closeCreateDifferentModal,
  } = useModal();

  const columns: NullableTableColumns<Rule> = {
    name: {
      label: t("general.table.name.column.title"),
      render: (rule) => (
        <Highlight text={rule.name} term={searchTerm} className="truncate text-sm" />
      ),
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    code: {
      label: t("general.table.code.column.title"),
      render: (rule) => (
        <Highlight text={rule.code} term={searchTerm} className="truncate text-sm" />
      ),
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    type: {
      label: t("general.table.type.column.title"),
      render: (rule) => (
        <Highlight
          text={getRuleImplementationTypeLabel(rule.implementationType)}
          term={searchTerm}
          className="truncate text-sm"
        />
      ),
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    description: {
      label: t("general.table.description.column.title"),
      render: (rule) => (
        <Highlight text={rule.description ?? ""} term={searchTerm} className="truncate text-sm" />
      ),
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    actions: {
      width: 100,
      label: "",
      render: (rule) => <RuleActionsMenu rule={rule} rules={rules ?? []} />,
    },
  };

  return (
    <>
      <DimensionedLayout>
        {dimensions?.width && (
          <FixedTable
            id="rulesTable"
            data={rules}
            columns={columns}
            isLoading={loading}
            pageSize={pageSize}
            isError={!!error}
            title={t("rules.title")}
            description={t("page.rules.table.description")}
            filter={filter}
            toolbar={() => (
              <TableToolbar aria-label="data table toolbar">
                <TableToolbarContent>
                  <TableToolbarSearch onSearchChange={handleSearch} search={searchTerm} />
                  <AuthorizedMenu
                    menuItemsPermissions={[
                      { resource: ScopeResource.RULE, scopes: [Scope.CREATE] },
                      { resource: ScopeResource.RULE, scopes: [Scope.ADVANCED] },
                    ]}
                  >
                    <MenuButton label={t("general.actions.create")} menuAlignment={"bottom-end"}>
                      <AuthorizedItem
                        requiredPermissions={[
                          { resource: ScopeResource.RULE, scopes: [Scope.CREATE] },
                        ]}
                      >
                        <MenuItem
                          label={t("page.rules.table.button.create")}
                          onClick={openCreateModal}
                        />
                      </AuthorizedItem>
                      <AuthorizedItem
                        requiredPermissions={[
                          { resource: ScopeResource.RULE, scopes: [Scope.ADVANCED] },
                        ]}
                      >
                        <MenuItem
                          label={t("page.rules.advanced.table.button.create")}
                          onClick={openCreateAdvancedModal}
                        />
                      </AuthorizedItem>
                      <AuthorizedItem
                        requiredPermissions={[
                          { resource: ScopeResource.RULE, scopes: [Scope.CREATE] },
                        ]}
                      >
                        <MenuItem
                          label={t("page.rules.linkedCase.table.button.create")}
                          onClick={() => {
                            openCreateDifferentModal();
                          }}
                        />
                      </AuthorizedItem>
                    </MenuButton>
                  </AuthorizedMenu>
                </TableToolbarContent>
              </TableToolbar>
            )}
          />
        )}
      </DimensionedLayout>

      <ModalTransition>
        {isCreateModalOpen && (
          <CreateRuleModal
            rules={rules ?? []}
            caseTypeCode={caseTypeCode}
            onClose={closeCreateModal}
          />
        )}
      </ModalTransition>
      <ModalTransition>
        {isCreateAdvancedModalOpen && (
          <CreateRuleAdvancedModal
            rules={rules ?? []}
            caseTypeCode={caseTypeCode}
            onClose={closeCreateAdvancedModal}
          />
        )}
      </ModalTransition>
      <ModalTransition>
        {isCreateDifferentModalOpen && (
          <CreateLinkedCaseRuleModal
            rules={rules}
            onClose={closeCreateDifferentModal}
            caseTypeCode={caseTypeCode}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default RulesTab;
